import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, payoffCard } from "@/lib/finance";
import { fmtUSD, fmtMonths } from "@/lib/format";

export default function CreditCardPage() {
    const [balance, setBalance] = useState(5000);
    const [apr, setApr] = useState(22.9);
    const [payment, setPayment] = useState(200);

    const result = useMemo(
        () => payoffCard(toNum(balance), toNum(apr), toNum(payment)),
        [balance, apr, payment],
    );
    const result2x = useMemo(
        () => payoffCard(toNum(balance), toNum(apr), toNum(payment) * 2),
        [balance, apr, payment],
    );

    return (
        <CalculatorShell
            slug="credit-card-payoff"
            explainer={
                                <>
                    <h2>How this credit card payoff calculator works</h2>
                    <p>
                        A credit card payoff calculator shows how long it takes to pay off a balance and how
                        much interest you'll pay given a fixed monthly payment. It also shows the true cost of
                        making only minimum payments — a number that often surprises people.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Daily periodic rate:</strong> APR divided by 365 — interest accrues daily on your remaining balance.</li>
                        <li><strong>Minimum payment design:</strong> minimum payments are intentionally structured to keep balances high and interest income flowing.</li>
                        <li><strong>Fixed payment mode:</strong> enter a monthly amount and see exactly when the balance hits zero.</li>
                        <li><strong>Target date mode:</strong> enter a payoff date and see the monthly payment required to hit it.</li>
                        <li><strong>Compounding interest:</strong> each month's interest is added to the balance before next month's interest is calculated.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Paying twice the minimum payment dramatically shortens the payoff timeline — often by years.</li>
                        <li>Pay minimums on all cards but one, then focus all extra dollars on that one card until it's gone.</li>
                        <li>A balance transfer to a 0% promotional card can eliminate interest — but only if you commit to paying it off before the promotional period ends.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Current balance" prefix="$" value={balance} onChange={setBalance} dataTestId="calc-input-balance" />
                    <Field label="APR" suffix="%" value={apr} onChange={setApr} step={0.1} dataTestId="calc-input-apr" />
                    <Field label="Monthly payment" prefix="$" value={payment} onChange={setPayment} dataTestId="calc-input-payment" />
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <ResultStat
                        label="Payoff time"
                        value={result.months}
                        format={(n) => fmtMonths(n)}
                        secondary={[
                            { label: "Total interest", value: result.totalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-interest" },
                            { label: "Total paid", value: result.totalInterest + toNum(balance), format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-total" },
                        ]}
                    />
                    <div className="bg-accent/10 border border-accent/30 rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-primary">
                            If you doubled your payment
                        </div>
                        <div className="font-mono tabular text-2xl text-primary mt-2" data-testid="calc-result-double">
                            {fmtMonths(result2x.months)}
                        </div>
                        <div className="text-xs text-primary/70 mt-1">
                            Interest saved: <span className="font-mono tabular font-medium">{fmtUSD(result.totalInterest - result2x.totalInterest, { decimals: 0 })}</span>
                        </div>
                    </div>
                </div>
            </div>
        </CalculatorShell>
    );
}
